import moment from 'moment'

import { apiService } from '@/api'
import { closeAlert } from '@/reducers/realtime/realtime.actions'
import { fetchActivityLogUsers } from '@/reducers/realtime/activityLog/activityLog.actions'
import {
  OPEN_RTC_SOCKET,
  CLOSE_RTC_SOCKET,
  UPDATE_SOCKET_STATUS,
  REFRESH_USERS_ALERTS,
  SEND_CHAT_TO_DESKTOP,
} from '@/reducers/realtime/realtimeSocket.redux'
import { LOAD_ALERTS } from '@/reducers/realtime/alerts/realtimeUserAlerts.redux'
import { ADD_MESSAGE_TO_CHAT, RAISE_HAND } from '@/reducers/realtime/realtimeChats.redux'
import {
  addTranscriptUtterance,
  endAgentTranscript,
} from '@/reducers/realtime/liveTranscripts.redux'
import { formatTranscriptUtterance } from '@/reducers/realtime/liveTranscripts.helpers'
import { LOGOUT_USER } from '@/reducers/auth/currentUser.redux'

// NOTE: Might require a fallback to use one client per tab and no shared worker if we need to
// support Safari at some point
export const rtcSocketMiddleware = (worker, broadcast) => (params) => (next) => (action) => {
  const { dispatch, getState } = params
  const { type: actionType, ...actionData } = action
  const { username, token, user_id } = getState().currentUser

  switch (actionType) {
    case OPEN_RTC_SOCKET:
      worker.init()
      broadcast.init()

      if (worker.unsupportedBrowser || broadcast.unsupportedBrowser) {
        return next(action)
      }

      broadcast.on('message', ({ data }) => {
        const { type: broadcastedType, ...broadcastedData } = data

        switch (broadcastedType) {
          // Whenever the connection status changes, the RTC alert sidebar handles the updates
          case 'CONNECTION_STATUS':
            dispatch({
              type: UPDATE_SOCKET_STATUS,
              isOpen: broadcastedData.isOpen,
              maxRetriesExceeded: broadcastedData.maxRetriesExceeded,
            })
            break

          // If a message comes through on the Broadcast Channel, handle it here
          // Can this be simplified by making each action + side effect have a type, as opposed
          // to each condition being based on what properties the message might have?
          case 'SOCKET_RECEIVED_MESSAGE': {
            const { message } = broadcastedData
            // Since the broadcasted messages don't have types, we'll make types to make this easier to read
            // NOTE: Since this is using an "else if" flow, each conditional is NOT guaranteed to be correct on its own
            const CALL_END = message.data?.stage === 'CHECK_END_CALL'
            const INCOMING_ALERT = message.processing_url
            const INCOMING_CHAT_MESSAGE = message.from_user_id && message.to_user_id
            // Every supervised agent's utterances arrive here whether or not
            // anyone is listening. Keep the ones for agents the supervisor has
            // an open chat with — that's the set the transcript panel can show,
            // and it deliberately does NOT depend on audio being on (QCER-1098).
            //
            // Redis fans one publish out to both of this manager's sockets, so
            // the agent the Command Center socket was asked to relay has to be
            // skipped here or the supervisor reads every sentence twice.
            const UPDATE_CHAT_TRANSCRIPTION =
              message.data?.transcript &&
              !getState().liveTranscripts?.watchedAgents?.includes(message.user) &&
              Boolean(getState().realtimeChats?.chats?.[message.user])
            const RAISED_HAND = message?.event === 'raised_hand'

            // If call end event comes through, dismiss the alert and refresh
            // the activity log. The chat conversation persists — managers
            // should be able to keep messaging an agent after the call ends.
            if (CALL_END) {
              if (window.location?.pathname === '/realtime_coaching/activity-log') {
                dispatch(fetchActivityLogUsers({ alertedUserId: user_id }))
              }
              // Clear the live alert (hides Listen once the call is over). The
              // chat itself persists — the manager closes it manually when done.
              dispatch(closeAlert({ username: message?.user }))
              // The transcript stays up, marked as finished, so the manager can
              // still read the call they were just following. The agent's next
              // call clears it.
              dispatch(endAgentTranscript(message?.user))
            }
            // Load the alert so the floating Listen/Chat panel has its live-call
            // context. The notification bell (NotificationProvider) owns the
            // audible ding now — this socket used to play it too, which double-
            // dinged accounts with both sockets open (same reason WEB_NOTIFICATION
            // was removed below).
            else if (INCOMING_ALERT) {
              dispatch({ type: LOAD_ALERTS, alert: message })
            }
            // If manager or agent sends a message
            else if (INCOMING_CHAT_MESSAGE) {
              dispatch({
                type: ADD_MESSAGE_TO_CHAT,
                agentUsername: message.agent_username,
                message: message.message,
                isMessageFromManager: false,
                date: moment().format('LT'),
                chatId: message.chat_id,
              })
            }
            // Add transcription
            else if (UPDATE_CHAT_TRANSCRIPTION) {
              dispatch(
                addTranscriptUtterance(message.user, formatTranscriptUtterance(message.data))
              )
            } else if (RAISED_HAND) {
              // Load the live alert for the Listen/Chat panel only — the bell
              // (NotificationProvider) plays the audible ding. Playing it here
              // too double-dinged managers who have both sockets open.
              dispatch({
                type: LOAD_ALERTS,
                alert: {
                  name: 'Raised Hand',
                  type: 'negative',
                  processing_url: 'raised_hand',
                  call_id: message.call_id,
                  alert_id: 'raised_hand',
                  user_id: message.agent_id,
                  manager_id: message.manager_id,
                  username: message.agent_username,
                  manager_username: message.manager_username,
                  manager_first_name: message.manager_first_name,
                  manager_last_name: message.manager_last_name,
                  user_first_name: message.agent_first_name,
                  user_last_name: message.agent_last_name,
                },
              })
              // Seed the Agent Assist list so the agent appears as a "needs
              // help" pill (and badges the launcher) immediately — before the
              // supervisor opens the panel. A repeat raise re-raises them.
              dispatch({
                type: RAISE_HAND,
                alertId: 'raised_hand',
                callId: message.call_id,
                agentId: message.agent_id,
                managerId: message.manager_id,
                agentUsername: message.agent_username,
                agentFirstName: message.agent_first_name,
                agentLastName: message.agent_last_name,
                managerUsername: message.manager_username,
                managerFirstName: message.manager_first_name,
                managerLastName: message.manager_last_name,
              })
            }
            // WEB_NOTIFICATION used to be handled here too. The dedicated
            // notification-service WebSocket (NotificationProvider) is now
            // the canonical path so non-RTC users get the bell. Keeping a
            // dispatch here would deliver the same notification twice on
            // accounts that have BOTH sockets open. Removed deliberately.
            break
          }

          default:
            break
        }
      })

      worker.post({
        token,
        username,
        type: actionType,
        url: `${apiService.rtc_websocket}/ws/realtime_coaching`,
      })
      break

    case REFRESH_USERS_ALERTS:
      worker.post({ type: actionType })
      break

    case SEND_CHAT_TO_DESKTOP:
      worker.post({ type: actionType, chatData: actionData })
      break

    case CLOSE_RTC_SOCKET:
    case LOGOUT_USER:
      if (worker.sharedWorker) {
        worker.post({ type: CLOSE_RTC_SOCKET })
      }

      if (broadcast.broadcastChannel) {
        broadcast.close()
      }

      break

    default:
      break
  }

  return next(action)
}
